import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { BASE_URL } from "../../../config/config";

// What OTS has earned, from every place it earns: online booking commission,
// the OTS fee on verified cash bookings, rent commission and late fees. The
// server does the arithmetic (Pakistan time); this only draws it.

const COLORS = {
  online_commission: "#2563eb",
  cash_fee: "#16a34a",
  rent_commission: "#f59e0b",
  late_fee: "#ef4444",
  advance_commission: "#8b5cf6",
};

const money = (n) => `Rs ${Number(n || 0).toLocaleString()}`;
const today = () => moment().format("YYYY-MM-DD");

const PRESETS = {
  today: () => ({ from: today(), to: today(), groupBy: "day" }),
  month: () => ({ from: moment().startOf("month").format("YYYY-MM-DD"), to: today(), groupBy: "day" }),
  lastMonth: () => ({
    from: moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
    to: moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
    groupBy: "day",
  }),
  last30: () => ({ from: moment().subtract(29, "days").format("YYYY-MM-DD"), to: today(), groupBy: "day" }),
  year: () => ({ from: moment().startOf("year").format("YYYY-MM-DD"), to: today(), groupBy: "month" }),
};

const niceMax = (v) => {
  if (v <= 0) return 1000;
  const p = 10 ** Math.floor(Math.log10(v));
  return Math.ceil(v / p) * p;
};

// Stacked bars, one per day/month, split by where the money came from.
function Chart({ series, sources, groupBy }) {
  const W = 820;
  const H = 260;
  const pad = { l: 56, r: 12, t: 12, b: 34 };
  const keys = Object.keys(sources);
  const max = niceMax(Math.max(0, ...series.map((b) => b.total)));
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;
  const step = innerW / Math.max(series.length, 1);
  const barW = Math.max(2, Math.min(36, step * 0.7));
  const y = (v) => pad.t + innerH - (v / max) * innerH;
  const labelEvery = Math.ceil(series.length / 12);

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", minWidth: 480, height: "auto" }} role="img" aria-label="Earnings chart">
        {[0, 0.25, 0.5, 0.75, 1].map((t) => (
          <g key={t}>
            <line x1={pad.l} x2={W - pad.r} y1={y(max * t)} y2={y(max * t)} stroke="#e5e7eb" />
            <text x={pad.l - 6} y={y(max * t) + 4} fontSize="10" textAnchor="end" fill="#64748b">
              {Math.round(max * t).toLocaleString()}
            </text>
          </g>
        ))}
        {series.map((b, i) => {
          const x = pad.l + i * step + (step - barW) / 2;
          let acc = 0;
          return (
            <g key={b.key}>
              <title>{`${b.key}: ${money(b.total)}`}</title>
              {keys.map((k) => {
                const v = b[k] || 0;
                if (!v) return null;
                const top = y(acc + v);
                const h = y(acc) - top;
                acc += v;
                return <rect key={k} x={x} y={top} width={barW} height={h} fill={COLORS[k]} rx={1} />;
              })}
              {i % labelEvery === 0 && (
                <text x={x + barW / 2} y={H - 12} fontSize="10" textAnchor="middle" fill="#64748b">
                  {groupBy === "month" ? moment(b.key, "YYYY-MM").format("MMM YY") : moment(b.key).format("D MMM")}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </Box>
  );
}

const Tile = ({ label, value, sub, color }) => (
  <Card sx={{ borderRadius: 3, border: `1px solid ${color}30`, height: "100%" }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, color }}>
        {money(value)}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      )}
    </CardContent>
  </Card>
);

export default function EarningsReport() {
  const [preset, setPreset] = useState("month");
  const [range, setRange] = useState(PRESETS.month());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [view, setView] = useState("summary");
  const [page, setPage] = useState(0);

  const load = useCallback(async (r) => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem("ots_token") || localStorage.getItem("ots_token");
      const { data } = await axios.get(`${BASE_URL}/admin/revenue/report`, {
        params: r,
        headers: { Authorization: `Bearer ${token}` },
      });
      setReport(data.data);
      setError("");
      setPage(0);
    } catch (e) {
      setError(e.response?.data?.message || "Failed to load the earnings report");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(range);
  }, [range, load]);

  const choosePreset = (p) => {
    setPreset(p);
    if (p !== "custom") setRange(PRESETS[p]());
  };

  const t = report?.totals;
  const growth = useMemo(() => {
    if (!t || !t.lastMonth) return null;
    return Math.round(((t.thisMonth - t.lastMonth) / t.lastMonth) * 100);
  }, [t]);

  const usedSources = report ? Object.keys(report.sources).filter((k) => report.bySource[k] > 0 || k !== "advance_commission") : [];

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, mb: 4, border: "1px solid #e5e7eb", borderRadius: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Earnings report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Everything OTS earned: online commission, cash-booking fees, rent commission and late fees.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Period</InputLabel>
            <Select value={preset} label="Period" onChange={(e) => choosePreset(e.target.value)}>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="month">This month</MenuItem>
              <MenuItem value="lastMonth">Last month</MenuItem>
              <MenuItem value="last30">Last 30 days</MenuItem>
              <MenuItem value="year">This year (by month)</MenuItem>
              <MenuItem value="custom">Custom…</MenuItem>
            </Select>
          </FormControl>
          {preset === "custom" && (
            <>
              <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} />
              <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} />
              <ToggleButtonGroup exclusive size="small" value={range.groupBy} onChange={(e, v) => v && setRange({ ...range, groupBy: v })}>
                <ToggleButton value="day">Daily</ToggleButton>
                <ToggleButton value="month">Monthly</ToggleButton>
              </ToggleButtonGroup>
            </>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !report ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : report ? (
        <Box sx={{ opacity: loading ? 0.5 : 1, transition: "opacity .2s" }}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <Tile label="Today" value={t.today} color="#2563eb" />
            </Grid>
            <Grid item xs={6} md={3}>
              <Tile
                label="This month"
                value={t.thisMonth}
                color="#16a34a"
                sub={growth == null ? null : `${growth >= 0 ? "▲" : "▼"} ${Math.abs(growth)}% vs last month`}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <Tile label="Last month" value={t.lastMonth} color="#64748b" />
            </Grid>
            <Grid item xs={6} md={3}>
              <Tile label={`${moment(report.range.from).format("D MMM")} – ${moment(report.range.to).format("D MMM YYYY")}`} value={t.range} color="#e94560" sub={`Online ${money(report.online)} · Cash ${money(report.cash)}`} />
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {usedSources.map((k) => (
              <Chip
                key={k}
                label={`${report.sources[k]}: ${money(report.bySource[k])}`}
                sx={{ bgcolor: `${COLORS[k]}18`, color: COLORS[k], fontWeight: 600, border: `1px solid ${COLORS[k]}40` }}
              />
            ))}
          </Box>

          <Chart series={report.series} sources={report.sources} groupBy={report.range.groupBy} />

          <Box sx={{ mt: 2, mb: 1 }}>
            <ToggleButtonGroup exclusive size="small" value={view} onChange={(e, v) => v && (setView(v), setPage(0))}>
              <ToggleButton value="summary" sx={{ textTransform: "none" }}>
                By {report.range.groupBy === "month" ? "month" : "day"}
              </ToggleButton>
              <ToggleButton value="payments" sx={{ textTransform: "none" }}>
                Every payment ({report.rows.length})
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {view === "summary" ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{report.range.groupBy === "month" ? "Month" : "Day"}</TableCell>
                    {usedSources.map((k) => (
                      <TableCell key={k} align="right">
                        {report.sources[k]}
                      </TableCell>
                    ))}
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...report.series]
                    .reverse()
                    .slice(page * 10, page * 10 + 10)
                    .map((b) => (
                      <TableRow key={b.key} hover>
                        <TableCell>{report.range.groupBy === "month" ? moment(b.key, "YYYY-MM").format("MMMM YYYY") : moment(b.key).format("ddd, D MMM YYYY")}</TableCell>
                        {usedSources.map((k) => (
                          <TableCell key={k} align="right">
                            {b[k] ? money(b[k]) : "—"}
                          </TableCell>
                        ))}
                        <TableCell align="right">
                          <strong>{money(b.total)}</strong>
                        </TableCell>
                      </TableRow>
                    ))}
                  <TableRow>
                    <TableCell>
                      <strong>Total</strong>
                    </TableCell>
                    {usedSources.map((k) => (
                      <TableCell key={k} align="right">
                        <strong>{money(report.bySource[k])}</strong>
                      </TableCell>
                    ))}
                    <TableCell align="right">
                      <strong>{money(t.range)}</strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <TablePagination component="div" count={report.series.length} page={page} onPageChange={(e, p) => setPage(p)} rowsPerPage={10} rowsPerPageOptions={[10]} />
            </TableContainer>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Tenant</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell>Property</TableCell>
                    <TableCell align="right">OTS earned</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.rows.slice(page * 10, page * 10 + 10).map((r, i) => (
                    <TableRow key={`${r.reference}-${r.source}-${i}`} hover>
                      <TableCell>{moment(r.date).format("DD MMM YYYY, h:mm A")}</TableCell>
                      <TableCell>
                        <Chip size="small" label={r.sourceLabel} sx={{ bgcolor: `${COLORS[r.source]}18`, color: COLORS[r.source], fontWeight: 600 }} />
                      </TableCell>
                      <TableCell>{r.tenantName || "—"}</TableCell>
                      <TableCell>{r.landlordName || "—"}</TableCell>
                      <TableCell>{r.flatName || "—"}</TableCell>
                      <TableCell align="right">
                        <strong>{money(r.amount)}</strong>
                      </TableCell>
                    </TableRow>
                  ))}
                  {report.rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                        No earnings in this period.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination component="div" count={report.rows.length} page={page} onPageChange={(e, p) => setPage(p)} rowsPerPage={10} rowsPerPageOptions={[10]} />
            </TableContainer>
          )}
        </Box>
      ) : null}
    </Paper>
  );
}
